import { useEffect, useState } from 'react';

export interface SiteSettings {
  brandShortName: string;
  brandFullName: string;
  siteUrl: string;
  primaryCtaLabel: string;
  primaryCtaPath: string;
  footerDescription: string;
  footerCopyright: string;
  contactEmail: string;
  supportEmail: string;
  phoneDisplay: string;
  phoneHref: string;
  contactWebhookUrl: string;
  bookingWebhookUrl: string;
  bookingTimezone: string;
  bookingTimezoneLabel: string;
  legalCompanyName: string;
  legalRepresentative: string;
  legalRegistrationId: string;
  legalAddress: string;
  legalCountry: string;
  hostingProviderName: string;
  hostingProviderAddress: string;
  facebookUrl: string;
  instagramUrl: string;
  linkedinUrl: string;
}

export const DEFAULT_SITE_SETTINGS: SiteSettings = {
  brandShortName: 'CSD',
  brandFullName: 'Couteau Suisse Digital',
  siteUrl: 'https://couteausuissedigital.com',
  primaryCtaLabel: 'Réservez un appel',
  primaryCtaPath: '/reserver',
  footerDescription: 'Votre partenaire dans la transformation digitale',
  footerCopyright: '© 2026 Couteau Suisse Digital. Tous droits réservés.',
  contactEmail: 'contact@couteausuissedigital.com',
  supportEmail: 'hello@couteausuissedigital.com',
  phoneDisplay: '+41 79 65 38 00',
  phoneHref: 'tel:+4179653800',
  contactWebhookUrl: 'https://hook.us1.make.com/a6cy89cnq482rl07p1g9wlzmd1k8mxt3',
  bookingWebhookUrl: 'https://hook.us1.make.com/lw31pdhtjwyka463kwgbdhwvkoimkhoi',
  bookingTimezone: 'America/Toronto',
  bookingTimezoneLabel: 'Toronto',
  legalCompanyName: 'Tybox Network',
  legalRepresentative: 'Couteau Suisse Digital',
  legalRegistrationId: 'CHE-499.459.668',
  legalAddress: 'Rue du college 4, 1400 Yverdon-les-Bains, Suisse',
  legalCountry: 'Suisse',
  hostingProviderName: 'Ouloulou',
  hostingProviderAddress: 'ftp.ouloulou.fr.fo',
  facebookUrl: 'https://www.facebook.com/couteausuissedigital',
  instagramUrl: 'https://www.instagram.com/couteausuissedigital_officiel/',
  linkedinUrl: '',
};

const CACHE_KEY = 'csd_site_settings_cache_v1';
const SETTINGS_URL = '/data/siteSettings.json';
const SAVE_URL = '/saveSiteSettings.php';

type SiteSettingsListener = (settings: SiteSettings) => void;

const listeners = new Set<SiteSettingsListener>();

const canUseBrowserStorage = () =>
  typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

const mergeSiteSettings = (input?: Partial<SiteSettings> | null): SiteSettings => ({
  ...DEFAULT_SITE_SETTINGS,
  ...(input || {}),
});

const readCachedSettings = (): SiteSettings => {
  if (!canUseBrowserStorage()) {
    return DEFAULT_SITE_SETTINGS;
  }

  try {
    const rawValue = window.localStorage.getItem(CACHE_KEY);
    if (!rawValue) {
      return DEFAULT_SITE_SETTINGS;
    }

    const parsedValue = JSON.parse(rawValue) as Partial<SiteSettings>;
    return mergeSiteSettings(parsedValue);
  } catch (error) {
    console.error('Impossible de lire le cache des settings site', error);
    return DEFAULT_SITE_SETTINGS;
  }
};

let cachedSettings = readCachedSettings();
let hasLoadedRemoteSettings = false;
let activeLoadPromise: Promise<SiteSettings> | null = null;

const broadcastSettings = (settings: SiteSettings) => {
  cachedSettings = settings;

  if (canUseBrowserStorage()) {
    window.localStorage.setItem(CACHE_KEY, JSON.stringify(settings));
  }

  for (const listener of listeners) {
    listener(settings);
  }
};

const parseResponse = async <T,>(response: Response): Promise<T> => {
  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    throw new Error(payload?.message || payload?.error || 'Erreur settings site');
  }

  return payload as T;
};

export const getSiteSettingsSync = () => cachedSettings;

export const subscribeToSiteSettings = (listener: SiteSettingsListener) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const loadSiteSettings = async (forceRefresh = false): Promise<SiteSettings> => {
  if (!forceRefresh && hasLoadedRemoteSettings) {
    return cachedSettings;
  }

  if (!forceRefresh && activeLoadPromise) {
    return activeLoadPromise;
  }

  const requestUrl = forceRefresh
    ? `${SETTINGS_URL}?t=${Date.now()}`
    : SETTINGS_URL;

  activeLoadPromise = fetch(requestUrl, {
    cache: 'no-store',
    credentials: 'same-origin',
  })
    .then(async (response) => {
      if (!response.ok) {
        throw new Error('Impossible de charger les settings site');
      }

      const payload = (await response.json()) as Partial<SiteSettings>;
      const nextSettings = mergeSiteSettings(payload);
      broadcastSettings(nextSettings);
      hasLoadedRemoteSettings = true;
      return nextSettings;
    })
    .catch((error) => {
      console.error(error);
      return cachedSettings;
    })
    .finally(() => {
      activeLoadPromise = null;
    });

  return activeLoadPromise;
};

export const saveSiteSettings = async (settings: SiteSettings): Promise<SiteSettings> => {
  const payload = mergeSiteSettings(settings);

  const response = await fetch(SAVE_URL, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  const result = await parseResponse<{ status: 'success'; settings: Partial<SiteSettings> }>(
    response
  );

  const nextSettings = mergeSiteSettings(result.settings);
  broadcastSettings(nextSettings);
  hasLoadedRemoteSettings = true;
  return nextSettings;
};

export const useSiteSettings = () => {
  const [settings, setSettings] = useState<SiteSettings>(getSiteSettingsSync());
  const [isLoading, setIsLoading] = useState(!hasLoadedRemoteSettings);

  useEffect(() => {
    const unsubscribe = subscribeToSiteSettings((nextSettings) => {
      setSettings(nextSettings);
      setIsLoading(false);
    });

    loadSiteSettings()
      .then((nextSettings) => {
        setSettings(nextSettings);
      })
      .finally(() => {
        setIsLoading(false);
      });

    return unsubscribe;
  }, []);

  return {
    settings,
    isLoading,
  };
};

export const siteSettingsService = {
  getSiteSettingsSync,
  loadSiteSettings,
  saveSiteSettings,
  subscribeToSiteSettings,
};
